import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQuotationStore } from '@/stores/quotationStore';
import type { Quotation } from '@/types/quotation';
import { BookTemplate, Tag, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface SaveTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quotation: Quotation | null;
}

export function SaveTemplateDialog({ open, onOpenChange, quotation }: SaveTemplateDialogProps) {
  const { toggleTemplateStatus } = useQuotationStore();
  const [templateName, setTemplateName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (quotation) {
      const defaultName =
        quotation.metadata?.templateName ||
        quotation.client?.projectName ||
        quotation.title ||
        `Template - ${quotation.metadata?.quoteNumber || 'Estimate'}`;
      setTemplateName(defaultName);
    }
  }, [quotation, open]);

  if (!quotation) return null;

  const isAlreadyTemplate = quotation.metadata?.isTemplate;

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    setIsSaving(true);
    try {
      await toggleTemplateStatus(quotation.metadata.quoteId, true, templateName.trim());
      toast.success(isAlreadyTemplate ? 'Template name updated' : 'Marked as Template successfully');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveTemplate = async () => {
    setIsSaving(true);
    try {
      await toggleTemplateStatus(quotation.metadata.quoteId, false);
      toast.success('Removed from Templates');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to remove template');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl p-6">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 text-amber-500 rounded-xl border border-amber-500/30">
              <BookTemplate className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">
                {isAlreadyTemplate ? 'Edit Template Name' : 'Name Your Template'}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Give this template a descriptive identifier to easily find it when building future projects.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5 text-amber-500" />
              Template Identifier Name
            </Label>
            <Input
              autoFocus
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              placeholder="e.g. 3BHK Premium Interior Baseline, Office Fitout Civil Work..."
              className="h-10 text-xs font-medium focus-visible:ring-amber-500 rounded-xl"
            />
          </div>

          <div className="p-3 bg-muted/40 rounded-xl border border-border text-[11px] text-muted-foreground">
            💡 <span className="font-semibold text-foreground">Tip:</span> Use descriptive names like package tier or project size so estimators can choose the right template instantly.
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {isAlreadyTemplate && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemoveTemplate}
              disabled={isSaving}
              className="text-rose-500 border-rose-200 hover:bg-rose-50 hover:text-rose-600 dark:border-rose-500/30 mr-auto text-xs font-semibold"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove Template
            </Button>
          )}

          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !templateName.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-5 text-xs rounded-xl"
          >
            <Check className="h-4 w-4 mr-1.5" />
            {isAlreadyTemplate ? 'Update Name' : 'Save as Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
